import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldWarning, CheckCircle, XCircle, ArrowCounterClockwise, Info, Wrench, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const SEV = {
  critical: "text-[var(--c-red)] border-[var(--c-red)]/50 bg-[var(--c-red)]/10",
  major: "text-[var(--c-amber)] border-[var(--c-amber)]/50 bg-[var(--c-amber)]/10",
  minor: "text-[var(--c-muted)] border-[var(--c-border)] bg-[var(--c-surface-2)]",
};

/** The pre-issue gate, with somewhere to actually resolve it. A blocking finding stops the
 *  sheet from being issued; the engineer either accepts the deviation with a justification
 *  (which is recorded against the project) or marks the design non-compliant. */
export default function SelfCheckDialog({ projectId, open, onOpenChange, gate, onGate }) {
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const [fixes, setFixes] = useState({});     // finding id → proposed nudge awaiting confirmation

  const load = useCallback(() => {
    if (!projectId) return;
    api.get(`/drawings/${projectId}/selfcheck`).then(({ data }) => onGate?.(data)).catch(() => {});
  }, [projectId, onGate]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Interference findings can be cleared here: propose the nudge, show what it does to the
  // rest of the layout, and only move anything once the engineer confirms.
  const proposeNudge = async (fid) => {
    setBusy(fid + "propose");
    try {
      const { data } = await api.post(`/drawings/${projectId}/selfcheck/clash-fix`,
        { finding_id: fid, confirm: false });
      setFixes((f) => ({ ...f, [fid]: data }));
      if (data.already_clear) { toast.success(data.message); load(); }
    } catch (e) { toast.error(fmtErr(e), { duration: 9000 }); } finally { setBusy(null); }
  };

  const applyNudge = async (fid) => {
    setBusy(fid + "apply");
    try {
      const { data } = await api.post(`/drawings/${projectId}/selfcheck/clash-fix`,
        { finding_id: fid, confirm: true });
      if (data.gate) onGate?.(data.gate); else load();
      setFixes((f) => ({ ...f, [fid]: null }));
      toast.success(data.message);
    } catch (e) { toast.error(fmtErr(e), { duration: 9000 }); } finally { setBusy(null); }
  };

  const decide = async (fid, action) => {
    try {
      setBusy(fid + action);
      const { data } = await api.post(`/drawings/${projectId}/selfcheck/decision`, {
        finding_id: fid, action, note: notes[fid] || "",
      });
      onGate?.(data);
      toast.success(action === "accept_deviation" ? "Deviation accepted and recorded"
        : action === "block_non_compliant" ? "Marked non-compliant — the sheet stays blocked"
          : "Decision cleared");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(null); }
  };

  const findings = gate?.findings || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="selfcheck-dialog"
        className="max-h-[86vh] max-w-3xl overflow-y-auto border-[var(--c-border)] bg-[var(--c-surface)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--c-text)]">
            <ShieldWarning size={18} className="text-[var(--c-accent)]" />
            Pre-issue self-check
            <span data-testid="selfcheck-status"
              className={`ml-1 rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase ${
                gate?.status === "block" ? SEV.critical : gate?.status === "warnings" ? SEV.major : SEV.minor}`}>
              {gate?.status === "block" ? "blocked" : gate?.status === "warnings" ? "advisory" : "pass"}
            </span>
          </DialogTitle>
        </DialogHeader>

        <p className="m-0 text-[12.5px] text-[var(--c-muted)]" data-testid="selfcheck-summary">
          {gate?.summary || "Running the gate…"} A sheet is not issued while a finding is blocking.
          Accepting a deviation records your justification against the project; the finding then
          shows as accepted on this and every later sheet.
        </p>

        {!findings.length && (
          <div className="rounded-lg border border-[var(--c-border)] p-4 text-[13px] text-[var(--c-text)]"
            data-testid="selfcheck-clear">
            <CheckCircle size={18} className="mb-1.5 text-[var(--c-green)]" />
            Every pre-issue check passed — nothing is holding the drawing set.
          </div>
        )}

        {findings.map((f) => (
          <div key={f.id} data-testid={`selfcheck-finding-${f.id}`}
            className={`rounded-lg border p-3 ${f.blocking ? "border-[var(--c-red)]/40" : "border-[var(--c-border)]"}`}>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className={`rounded-sm border px-1.5 py-0.5 font-mono text-[9px] uppercase ${SEV[f.severity] || SEV.minor}`}>
                {f.severity}
              </span>
              <span className="font-mono text-[10px] text-[var(--c-accent)]">{f.id}</span>
              {f.clause && <span className="font-mono text-[9px] text-[var(--c-muted)]">{f.clause}</span>}
              {f.blocking && (
                <span className="rounded-sm border border-[var(--c-red)]/50 px-1.5 py-0.5 font-mono text-[9px] text-[var(--c-red)]">
                  blocking
                </span>
              )}
              {f.accepted && (
                <span className="rounded-sm border border-[var(--c-green)]/40 px-1.5 py-0.5 font-mono text-[9px] text-[var(--c-green)]">
                  accepted
                </span>
              )}
            </div>
            <div className="text-[13.5px] font-medium text-[var(--c-text)]">{f.title}</div>
            <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-[var(--c-muted)]">{f.detail}</p>
            {(f.actual || f.required) && (
              <div className="mt-1 font-mono text-[10.5px] text-[var(--c-muted)]">
                {f.required != null && <>required <span className="text-[var(--c-text)]">{f.required}</span> · </>}
                {f.actual != null && <>actual <span className="text-[var(--c-text)]">{f.actual}</span></>}
              </div>
            )}
            {f.decision && (
              <div className="mt-2 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-2 py-1 text-[11.5px] text-[var(--c-muted)]"
                data-testid={`selfcheck-decision-${f.id}`}>
                <b className="text-[var(--c-text)]">
                  {f.decision.action === "accept_deviation" ? "Deviation accepted" : "Marked non-compliant"}
                </b>{" "}
                by {f.decision.by || "—"} · {(f.decision.at || "").slice(0, 10)}
                {f.decision.note ? ` — "${f.decision.note}"` : ""}
              </div>
            )}

            {String(f.id).startsWith("clash:") && !f.accepted && (
              <div className="mt-2 rounded-sm border border-[var(--c-accent)]/35 bg-[var(--c-accent)]/[0.07] p-2.5"
                data-testid={`clash-fix-${f.id}`}>
                {!fixes[f.id] && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[12px] text-[var(--c-text)]">
                      Clear it here — move the fitting just far enough to free the overlap.
                    </span>
                    <button data-testid={`clash-fix-propose-${f.id}`} disabled={busy === f.id + "propose"}
                      onClick={() => proposeNudge(f.id)}
                      className="rounded-sm bg-[var(--c-accent)] px-2.5 py-1 text-[11px] font-medium text-[var(--c-bg)] disabled:opacity-50">
                      {busy === f.id + "propose" ? "Measuring…" : "Propose fix"}
                    </button>
                  </div>
                )}
                {fixes[f.id] && fixes[f.id].ok && !fixes[f.id].already_clear && (
                  <div data-testid={`clash-fix-proposal-${f.id}`}>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--c-accent)]">
                      Proposed change — confirm to apply
                    </div>
                    <div className="mt-1 font-mono text-[12px] text-[var(--c-text)]">
                      {fixes[f.id].move} {fixes[f.id].delta_mm > 0 ? "+" : ""}
                      {fixes[f.id].delta_mm} mm in {fixes[f.id].axis} — clear of {fixes[f.id].clear_of}
                    </div>
                    <div className="mt-1 text-[11px] text-[var(--c-muted)]">
                      Interferences {fixes[f.id].clashes_now} → {fixes[f.id].clashes_after}
                      {fixes[f.id].resolves_this ? " · clears this one" : " · does not clear this one"}
                      {(fixes[f.id].also_resolves || []).length > 0 && (
                        <b className="text-[var(--c-green)]" data-testid={`clash-fix-also-${f.id}`}>
                          {" "}· also clears {fixes[f.id].also_resolves.join(", ")} — apply this one only
                        </b>
                      )}
                      {(fixes[f.id].introduces || []).length > 0 && (
                        <b className="text-[var(--c-amber)]">
                          {" "}· introduces {fixes[f.id].introduces.join(", ")}
                        </b>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button data-testid={`clash-fix-apply-${f.id}`} disabled={busy === f.id + "apply"}
                        onClick={() => applyNudge(f.id)}
                        className="rounded-sm bg-[var(--c-green)] px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50">
                        {busy === f.id + "apply" ? "Moving…" : "Confirm and move"}
                      </button>
                      <button data-testid={`clash-fix-cancel-${f.id}`}
                        onClick={() => setFixes((x) => ({ ...x, [f.id]: null }))}
                        className="rounded-sm border border-[var(--c-border)] px-2.5 py-1 text-[11px] text-[var(--c-text)]">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {fixes[f.id] && !fixes[f.id].ok && (
                  <div className="text-[12px] text-[var(--c-muted)]">{fixes[f.id].message}</div>
                )}
              </div>
            )}

            {f.fix && (
              <div className="mt-2 rounded-sm border border-[var(--c-accent)]/35 bg-[var(--c-accent)]/[0.07] p-2.5"
                data-testid={`selfcheck-fix-${f.id}`}>
                <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-[var(--c-accent)]">
                  <Wrench size={11} /> Where to fix it
                </div>
                <p className="m-0 text-[12px] leading-relaxed text-[var(--c-text)]">{f.fix.how}</p>
                {!!(f.fix.fields || []).length && (
                  <div className="mt-2 space-y-1.5">
                    {f.fix.fields.map((fl) => (
                      <div key={fl.id} data-testid={`selfcheck-fix-field-${f.id}-${fl.id}`}
                        className="flex flex-wrap items-center gap-2 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-2 py-1.5">
                        <span className="text-[12px] text-[var(--c-text)]">{fl.label}</span>
                        <span className="font-mono text-[10.5px] text-[var(--c-muted)]">
                          now {fl.current ?? "—"}{fl.unit ? ` ${fl.unit}` : ""}
                          {fl.suggested != null && (
                            <> → <span className="text-[var(--c-accent)]">{fl.suggested}{fl.unit ? ` ${fl.unit}` : ""}</span></>
                          )}
                        </span>
                        {fl.source === "validation" && (
                          <span data-testid={`selfcheck-fix-source-${f.id}-${fl.id}`}
                            title={`The validation engine is the single source of truth for this field${fl.self_check_value != null ? ` — the drawing check's own figure of ${fl.self_check_value} was superseded` : ""}`}
                            className="rounded-sm border border-[var(--c-green)]/50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-[var(--c-green)]">
                            per Validation{fl.check_id ? ` ${fl.check_id}` : ""}
                          </span>
                        )}
                        {fl.note && <span className="text-[10.5px] text-[var(--c-muted)]">{fl.note}</span>}
                        <Link data-testid={`selfcheck-fix-open-${f.id}-${fl.id}`}
                          to={`/projects/${projectId}?tab=${f.fix.tab || "intake"}&focus=${fl.id}`
                            + (fl.suggested != null ? `&suggest=${encodeURIComponent(fl.suggested)}` : "")
                            + `&why=${encodeURIComponent(`${f.id} — ${fl.note || f.title || "self-check finding"}`.slice(0, 90))}`}
                          onClick={() => onOpenChange(false)}
                          className="ml-auto flex h-6 items-center gap-1 rounded-sm border border-[var(--c-accent)]/50 px-2 text-[10px] text-[var(--c-accent)] hover:bg-[var(--c-accent)]/10">
                          Take me there <ArrowRight size={10} />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
                <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-[var(--c-muted)]">
                  <b className="text-[var(--c-text)]">Impact:</b> {f.fix.impact}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Link data-testid={`selfcheck-fix-validation-${f.id}`}
                    to={`/projects/${projectId}?tab=validation${f.fix.validation_domain ? `&domain=${f.fix.validation_domain}` : ""}`}
                    onClick={() => onOpenChange(false)}
                    className="flex h-6 items-center gap-1 rounded-sm border border-[var(--c-border)] px-2 text-[10px] text-[var(--c-text)] hover:border-[var(--c-accent)]">
                    See the impact in Validation <ArrowRight size={10} />
                  </Link>
                  {f.fix.tab === "design" && (
                    <Link data-testid={`selfcheck-fix-design-${f.id}`} to={`/projects/${projectId}?tab=design`}
                      onClick={() => onOpenChange(false)}
                      className="flex h-6 items-center gap-1 rounded-sm border border-[var(--c-border)] px-2 text-[10px] text-[var(--c-text)] hover:border-[var(--c-accent)]">
                      Open the Design tab <ArrowRight size={10} />
                    </Link>
                  )}
                </div>
              </div>
            )}

            {f.requires_decision ? (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {!f.decision && (
                  <input data-testid={`selfcheck-note-${f.id}`} value={notes[f.id] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [f.id]: e.target.value }))}
                    placeholder="Justification — route survey, client addendum, approved deviation…"
                    className="h-7 min-w-0 flex-1 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-2 text-[11.5px] text-[var(--c-text)]" />
                )}
                {!f.decision && (
                  <>
                    <button data-testid={`selfcheck-accept-${f.id}`} disabled={busy === f.id + "accept_deviation"}
                      onClick={() => decide(f.id, "accept_deviation")}
                      className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-green)]/50 px-2.5 text-[11px] text-[var(--c-green)] hover:bg-[var(--c-green)]/10 disabled:opacity-40">
                      <CheckCircle size={12} /> Accept deviation
                    </button>
                    <button data-testid={`selfcheck-block-${f.id}`} disabled={busy === f.id + "block_non_compliant"}
                      onClick={() => decide(f.id, "block_non_compliant")}
                      className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-red)]/50 px-2.5 text-[11px] text-[var(--c-red)] hover:bg-[var(--c-red)]/10 disabled:opacity-40">
                      <XCircle size={12} /> Non-compliant
                    </button>
                  </>
                )}
                {f.decision && (
                  <button data-testid={`selfcheck-clear-${f.id}`} disabled={busy === f.id + "clear"}
                    onClick={() => decide(f.id, "clear")}
                    className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] disabled:opacity-40">
                    <ArrowCounterClockwise size={12} /> Change the decision
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[var(--c-muted)]">
                <Info size={12} />
                {f.blocking
                  ? "This one cannot be accepted — it has to be fixed in the design, not signed off."
                  : f.severity === "critical"
                    ? "Worth fixing before issue, but it does not hold the drawing."
                    : "Advisory — it does not hold the drawing."}
              </div>
            )}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
